import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// components
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFSwitch, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
// types
import { setError } from 'src/redux/slices/error';
import {
  fNumberInput,
  fStringInputWithoutSpecialCharacters,
  fNumberInputMinMax,
} from 'src/utils/format-input';
import { CreateTaxRateDto, TaxRateDto } from '../../../api';
import { useLocales } from '../../../locales';
import { useDispatch } from '../../../redux/store';
import { addTaxEntity, getTaxRates } from '../../../redux/slices/tax';
import BookkeepingEditItem from './bookkeeping-tax-edit-item';

// ----------------------------------------------------------------------

type Props = {
  taxRates: TaxRateDto[];
};

export default function BookkeepingEditForm({ taxRates }: Props) {
  const mdUp = useResponsive('up', 'md');

  const { t } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const newSchema = Yup.object().shape({
    name: Yup.string(),
    value: Yup.number(),
    accountId: Yup.number(),
    taxIncluded: Yup.boolean(),
    list: Yup.array(),
  });

  const defaultValues = useMemo(
    () => ({
      name: undefined,
      value: undefined,
      accountId: undefined,
      taxIncluded: false,
      list: taxRates,
    }),
    [taxRates]
  );

  const methods = useForm({
    resolver: yupResolver(newSchema),
    defaultValues,
  });

  const { reset, setValue, watch } = methods;

  const values = watch();

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const dispatch = useDispatch();

  const [vatDialogOpen, setVatDialogOpen] = useState(false);

  const handleVatDialogOpen = () => {
    setVatDialogOpen(true);
  };

  const handleVatDialogClose = () => {
    setVatDialogOpen(false);
  };

  const createVat = (data: CreateTaxRateDto) => {
    try {
      if (data.name === undefined) {
        throw Error(t('Vat name is not set'));
      }
      if (data.value === undefined) {
        throw Error(t('Vat rate is not set'));
      }
      if (data.accountId === undefined) {
        throw Error(t('Vat account is not set'));
      }

      if (
        values.list &&
        values.list.find((x) => x.value === data.value && x.accountId === data.accountId) !==
          undefined
      ) {
        throw Error(t('Vat rate is already set to this account'));
      }

      setVatDialogOpen(false);

      dispatch(addTaxEntity(data)).then(() => dispatch(getTaxRates()));
      values.name = undefined;
      values.value = undefined;
      values.accountId = undefined;

      enqueueSnackbar(t('Create success!'));
    } catch (error) {
      dispatch(setError(error));
    }
  };

  const renderVatDialog = (
    <Dialog open={vatDialogOpen} onClose={handleVatDialogClose} slotProps={{}}>
      <DialogTitle>{t('Add VAT Rate')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ my: 1 }}>
          {t(
            'Here you add a custom VAT percentage, assign a user-friendly name, link it to an account, and choose whether or not the prices of products within this VAT will be registered including VAT.'
          )}
        </DialogContentText>
        <Stack spacing={1.5}>
          <Stack>
            <RHFTextField
              name="name"
              sx={{ my: 1 }}
              label={t('VAT Name')}
              type="text"
              value={values.name}
              onChange={(event) =>
                fStringInputWithoutSpecialCharacters({
                  name: 'name',
                  setValue,
                  value: event.target.value,
                })
              }
            />
          </Stack>

          <Stack>
            <RHFTextField
              name="value"
              placeholder="0.00"
              sx={{ my: 1 }}
              label={t('VAT (%)')}
              type="string"
              value={values.value === 0 ? '' : values.value}
              InputProps={{
                inputProps: {
                  min: 0,
                  max: 100,
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      %
                    </Box>
                  </InputAdornment>
                ),
              }}
              onChange={(event) =>
                fNumberInputMinMax({
                  name: 'value',
                  setValue,
                  value: event.target.value,
                  min: 0,
                  max: 100,
                })
              }
            />
          </Stack>

          <Stack>
            <RHFTextField
              name="accountId"
              sx={{ my: 1 }}
              label={t('VAT account number')}
              type="string"
              value={values.accountId ?? ''}
              InputProps={{
                inputProps: {
                  min: 0,
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      #
                    </Box>
                  </InputAdornment>
                ),
              }}
              onChange={(event) =>
                fNumberInput({ name: 'accountId', setValue, value: event.target.value })
              }
            />
          </Stack>

          <RHFSwitch
            name="taxIncluded"
            value={values.taxIncluded}
            label={t('All sales prices will be registerated including VAT')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleVatDialogClose}>{t('Cancel')}</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            createVat({
              name: values.name,
              value: values.value,
              accountId: values.accountId,
              inclusive: values.taxIncluded,
            })
          }
        >
          {t('Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderVat = (
    <>
      {mdUp && (
        <Grid size={{ md: 4 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {t('VAT')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t(
              "Here you can add pre-defined VAT brackets and assign them accounts corresponding to your bookkeeping providers IDs. If a VAT entry is added with the 'Sales price including VAT' selector, then all sales prices for products within this bracket should be registered with VAT."
            )}
          </Typography>
        </Grid>
      )}

      <Grid size={{ xs: 12, md: 8 }}>
        <Card>
          {!mdUp && <CardHeader title="VAT" />}

          <Stack spacing={3} sx={{ p: 3 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Iconify icon="eva:plus-fill" />}
              sx={{
                minWidth: 0,
                p: 1.5,
                width: 'fit-content',
                height: 'min-content',
                alignItems: 'right',
                margin: 'auto 0 auto auto',
              }}
              onClick={() => handleVatDialogOpen()}
            >
              {t('New')}
            </Button>

            {values.list && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">{t('VAT Brackets')}</Typography>
                <Stack>
                  {values.list.map(
                    (tx, index) =>
                      tx.id !== undefined &&
                      tx.value !== undefined && (
                        <BookkeepingEditItem
                          key={tx.id}
                          taxRate={tx}
                          index={index}
                          setValue={setValue}
                        />
                      )
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Card>
      </Grid>
    </>
  );

  return (
    <FormProvider methods={methods}>
      {renderVatDialog}

      <Grid container spacing={3}>
        {renderVat}
      </Grid>
    </FormProvider>
  );
}
